import React from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { ArrowRightIcon } from '@heroicons/react/outline'
import clsx from 'clsx'
import Warning from '../../../svg/Warning'
import useScreen from '../../../hooks/useScreen'

const StatLink = ({ to, title, count, isHighlighted }) => {
    return (
        <Link
            className={clsx(
                'h-full w-40 bg-white border-gray hover:text-black border-2 rounded-lg flex flex-col justify-evenly items-center p-4',
                isHighlighted &&
                    'bg-pgTileHighlight border-borderTileHighlight text-error hover:text-error',
            )}
            to={to}
        >
            <p className="text-3xl font-medium">{count}</p>
            <p className="text-sm">{title}</p>
        </Link>
    )
}

StatLink.propTypes = {
    to: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    title: PropTypes.string,
    count: PropTypes.number,
    isHighlighted: PropTypes.bool,
}

const Stats = ({
    to,
    icon,
    title,
    items,
    showAlert,
    emptyAlert,
    isOffline,
}) => {
    const { isMobile, isTablet } = useScreen()
    return (
        <div className="w-full flex-grow h-full border border-grayLines bg-white rounded-2xl flex flex-col items-start justify-start">
            <div className="flex-grow py-6 pl-4 pr-3 flex items-center justify-start gap-x-4">
                <div className="w-8 h-8 min-w-8 min-h-8 rounded-md overflow-hidden">
                    {icon}
                </div>
                <p className="font-normal text-lg">{title}</p>
            </div>
            <div className="w-full h-30 bg-pgBackground flex flex-row border-t border-grayLines justify-evenly items-center p-4 gap-4">
                {items.map(item => {
                    return (
                        <StatLink
                            key={item?.title}
                            to={item?.link}
                            title={item?.title}
                            count={item?.count}
                            isHighlighted={item?.isHighlighted}
                        />
                    )
                })}
            </div>
            {(showAlert || emptyAlert) && (
                <div className="w-full bg-pgBackground">
                    <div
                        className={clsx(
                            'w-full transition-all mb-4',
                            showAlert && isMobile && !emptyAlert
                                ? 'px-6 h-8'
                                : isTablet
                                ? 'px-36 h-8'
                                : isOffline
                                ? 'px-4 h-7'
                                : 'px-4 h-10',
                        )}
                    >
                        {!emptyAlert && (
                            <div className="w-full h-full bg-white rounded-lg">
                                <div className="w-full  h-full flex flex-row items-center justify-center p-2 gap-1 bg-yellowLight border rounded-lg border-yellowBorder">
                                    <Warning />
                                    {isOffline ? (
                                        <p className="w-full text-xs font-normal text-textDarkerGray text-center">
                                            Pending requests will sync once
                                            you&apos;re back online
                                        </p>
                                    ) : (
                                        <p className="w-full text-xs font-normal text-textDarkerGray text-center">
                                            You&apos;re back online! Your
                                            pending repair requests and parts
                                            orders are now being synced
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
            <Link
                to={to}
                className="flex-grow w-full max-h-14 py-4 px-6 flex items-center justify-between border-t border-grayLines"
            >
                <p className="text-black90 font-normal text-base">View all</p>
                <p className="text-black90 font-normal text-base">
                    <ArrowRightIcon className="w-3 h-3 text-textDarkGray" />
                </p>
            </Link>
        </div>
    )
}

Stats.propTypes = {
    to: PropTypes.string,
    icon: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.element,
        PropTypes.node,
    ]),
    title: PropTypes.string,
    items: PropTypes.array,
    showAlert: PropTypes.bool,
    emptyAlert: PropTypes.bool,
    isOffline: PropTypes.bool,
}

export default Stats
